<template>
  <b-modal
    id="review-nomination-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    title="Confirm and grant award"
    body-class="p-0"
    @hidden="resetModal"
  >
    <!-- PROGRAM Step 1: Review nominations -->
    <template v-if="isProgram && step === 1">
      <div class="px-4 py-4 step-content">
        <div class="nominee-header">
          <div class="nominee-header__badge">
            <img
              v-if="programDetail?.badge_presigned_url"
              :src="programDetail.badge_presigned_url"
              :alt="nomination?.program_name"
              class="nominee-header__badge-image"
            />
            <font-awesome-icon v-else :icon="['fad', 'award']" class="nominee-header__badge-icon" />
          </div>
          <div>
            <div class="nominee-header__name">{{ nomination?.full_name }}</div>
            <div class="nominee-header__email">{{ nomination?.email }}</div>
          </div>
        </div>

        <div class="nominee-tags">
          <b-badge pill variant="primary" class="nominee-tags__badge">{{ nomination?.program_name }}</b-badge>
          <b-badge v-if="nomination?.nominated_by?.length > 1" pill variant="info" class="nominee-tags__badge">
            <font-awesome-icon :icon="['fal', 'users']" class="mr-1" />
            x{{ nomination.nominated_by.length }} nominations
          </b-badge>
        </div>

        <div class="nomination-messages">
          <div
            v-for="nom in nomination?.nominated_by || []"
            :key="nom.recognition_nomination_id"
            class="nomination-message"
          >
            <div class="nomination-message__header">
              <font-awesome-icon :icon="['fal', 'comment']" class="nomination-message__icon" />
              <span class="nomination-message__author">
                On {{ formatDate(nom.created_at) }} {{ nom.full_name }} wrote:
              </span>
            </div>
            <p class="nomination-message__text">{{ nom.reviewer_details || 'No message provided.' }}</p>
          </div>
        </div>
      </div>

      <div class="px-4 py-3 footer-container">
        <b-button variant="outline-danger" :disabled="isSubmitting" @click="onReject">
          <b-spinner v-if="isRejecting" small class="mr-1" />
          Reject nomination
        </b-button>
        <b-button variant="primary" @click="step = 2">Grant award</b-button>
      </div>
    </template>

    <!-- PROGRAM Step 2: Grant details -->
    <template v-else-if="isProgram && step === 2">
      <div class="px-4 py-4 step-content">
        <a href="#" class="back-link" @click.prevent="step = 1">
          <font-awesome-icon :icon="['fal', 'arrow-left']" class="mr-1" />
          Back
        </a>

        <b-alert v-if="errorMessage" variant="danger" show class="mt-3 mb-3">
          {{ errorMessage }}
        </b-alert>

        <div class="mb-4">
          <div class="section-label">Add a personal message</div>
          <b-form-textarea
            v-model="recipientMessage"
            placeholder="Write a message to the recipient..."
            rows="3"
            max-rows="6"
          />
        </div>

        <div v-if="availableAmounts.length > 0" class="mb-3">
          <b-form-checkbox v-model="includeReward" @change="onRewardToggle">
            <strong>Award value</strong>
            <br />
            <small class="text-muted">Select the value they'll receive</small>
          </b-form-checkbox>

          <div v-if="includeReward" class="amount-pills mt-2 ml-4">
            <span
              v-for="amount in availableAmounts"
              :key="amount"
              :class="['amount-pill', { 'amount-pill--selected': selectedAmountMinor === amount }]"
              role="button"
              @click="selectedAmountMinor = amount"
            >
              {{ formatAmount(amount) }}
            </span>
          </div>
        </div>

        <adjustable-reward-input
          v-else-if="isAdjustableAmount"
          heading="Award value"
          :include-reward="includeReward"
          :typed-award-amount="typedAwardAmount"
          :max-award-value-minor="maxAwardValueMinor"
          :error="adjustableAmountError"
          @toggle="onRewardToggle"
          @input="onTypedAmountInput"
        />

        <div v-if="showCategorySelector" class="mb-3">
          <div class="section-label">Spend category<span v-if="categoryRequired"> *</span></div>
          <div class="value-chips">
            <span
              v-for="category in availableCategories"
              :key="category"
              :class="['value-chip', { 'value-chip--selected': selectedCategory === category }]"
              role="button"
              @click="toggleCategory(category)"
            >
              {{ category }}
            </span>
          </div>
          <small v-if="categoryRequired && !selectedCategory" class="text-danger">A category is required.</small>
        </div>

        <b-form-checkbox v-if="nomination?.nominated_by?.length > 1" v-model="rejectOthers" class="mt-3">
          <strong>Reject all other nominations</strong>
          <br />
          <small class="text-muted">
            Reject {{ nomination.nominated_by.length - 1 }} other
            {{ nomination.nominated_by.length - 1 === 1 ? 'nomination' : 'nominations' }} for this award
          </small>
        </b-form-checkbox>
      </div>

      <div class="px-4 py-3 footer-container">
        <b-button variant="outline-primary" @click="modalRef?.hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="!canSubmit" @click="onSubmit">
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          {{ submitButtonText }}
        </b-button>
      </div>
    </template>

    <!-- OPEN Step 1: Review & grant -->
    <template v-else-if="isOpen && step === 1">
      <div class="px-4 py-4 step-content">
        <div class="nominee-header">
          <div>
            <div class="nominee-header__name">{{ nomination?.full_name }}</div>
            <div class="nominee-header__email">{{ nomination?.email }}</div>
          </div>
        </div>

        <div class="nominee-tags">
          <b-badge pill variant="primary" class="nominee-tags__badge">{{ nomination?.program_name }}</b-badge>
        </div>

        <b-alert v-if="errorMessage" variant="danger" show class="mt-3 mb-3">
          {{ errorMessage }}
        </b-alert>

        <div v-if="senderNomination" class="sender-message">
          <div class="sender-message__label">{{ senderNomination.full_name }}'s message</div>
          <div class="sender-message__quote">
            {{ senderNomination.reviewer_details || 'No message provided.' }}
          </div>
        </div>

        <div v-if="availableAmounts.length > 0" class="mb-3">
          <b-form-checkbox v-model="includeReward" @change="onRewardToggle">
            <strong>Approve reward</strong>
            <br />
            <small class="text-muted">Select the value they'll receive</small>
          </b-form-checkbox>

          <div v-if="includeReward" class="amount-pills mt-2 ml-4">
            <span
              v-for="amount in availableAmounts"
              :key="amount"
              :class="['amount-pill', { 'amount-pill--selected': selectedAmountMinor === amount }]"
              role="button"
              @click="selectedAmountMinor = amount"
            >
              {{ formatAmount(amount) }}
            </span>
          </div>
        </div>

        <adjustable-reward-input
          v-else-if="isAdjustableAmount"
          heading="Approve reward"
          :include-reward="includeReward"
          :typed-award-amount="typedAwardAmount"
          :max-award-value-minor="maxAwardValueMinor"
          :error="adjustableAmountError"
          @toggle="onRewardToggle"
          @input="onTypedAmountInput"
        />

        <div v-if="showCategorySelector" class="mb-3">
          <div class="section-label">Spend category<span v-if="categoryRequired"> *</span></div>
          <div class="value-chips">
            <span
              v-for="category in availableCategories"
              :key="category"
              :class="['value-chip', { 'value-chip--selected': selectedCategory === category }]"
              role="button"
              @click="toggleCategory(category)"
            >
              {{ category }}
            </span>
          </div>
          <small v-if="categoryRequired && !selectedCategory" class="text-danger">A category is required.</small>
        </div>
      </div>

      <div class="px-4 py-3 footer-container">
        <b-button variant="outline-primary" @click="modalRef?.hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="!canSubmit" @click="onSubmit">
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          {{ submitButtonText }}
        </b-button>
      </div>
    </template>

    <!-- Success step (shared) -->
    <template v-if="step === 'success'">
      <div class="px-4 py-5 text-center">
        <div class="success-icon">
          <font-awesome-icon :icon="['fal', 'circle-check']" />
        </div>
        <h5 class="mt-3"><strong>Award sent</strong></h5>
        <p class="text-muted">{{ nomination?.full_name }} has been notified</p>
      </div>

      <div class="px-4 py-3 footer-container footer-container--right">
        <b-button variant="primary" @click="onDone">Done</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { RECOGNITION_AWARD_TYPES } from '@/pages/recognition/constants';
import { toCurrency } from '@/utils';
import useToast from '@/composables/useToast';
import AdjustableRewardInput from '@/pages/recognition/components/AdjustableRewardInput.vue';

const { doToast, TOAST_VARIANT } = useToast();

const props = defineProps({
  nomination: {
    type: Object,
    default: null,
  },
  programs: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['approved', 'rejected']);

const modalRef = ref(null);
const step = ref(1);
const recipientMessage = ref('');
const selectedAmountMinor = ref(null);
const typedAwardAmount = ref('');
const includeReward = ref(false);
const selectedCategory = ref(null);
const rejectOthers = ref(false);
const isSubmitting = ref(false);
const isRejecting = ref(false);
const errorMessage = ref('');

const programDetail = computed(() =>
  props.programs.find(p => p.recognition_award_program_id === props.nomination?.recognition_award_program_id),
);

const isProgram = computed(() => programDetail.value?.program_type === RECOGNITION_AWARD_TYPES.PROGRAM);

const isOpen = computed(() => programDetail.value?.program_type === RECOGNITION_AWARD_TYPES.OPEN);

const availableAmounts = computed(() => {
  const vals = programDetail.value?.properties?.award_value_minor;
  if (!vals) {
    return [];
  }
  return Array.isArray(vals) ? vals : [vals];
});

const defaultAmount = computed(() => programDetail.value?.properties?.default_award_value_minor || null);

const availableCategories = computed(() => programDetail.value?.properties?.categories || []);

const categoryRequired = computed(() => Boolean(programDetail.value?.properties?.category_required));

// The spend category is chosen by the approver only when a monetary reward is being
// granted and the program has categories configured.
const showCategorySelector = computed(() => includeReward.value && availableCategories.value.length > 0);

const maxAwardValueMinor = computed(() => programDetail.value?.properties?.max_award_value_minor ?? null);

// Adjustable mode: no fixed presets but a monetary reward with a cap (mirrors the
// mobile free-amount mode). The approver types the amount, bounded by the cap.
const isAdjustableAmount = computed(() => availableAmounts.value.length === 0 && maxAwardValueMinor.value != null);

const adjustableAmountError = computed(() => {
  if (!includeReward.value || !isAdjustableAmount.value) {
    return '';
  }
  const minor = selectedAmountMinor.value;
  if (!minor || minor <= 0) {
    return 'Enter an amount greater than 0.';
  }
  if (maxAwardValueMinor.value != null && minor > maxAwardValueMinor.value) {
    return `Amount cannot exceed ${formatAmount(maxAwardValueMinor.value)}.`;
  }
  return '';
});

const allNominationIds = computed(() => (props.nomination?.nominated_by || []).map(n => n.recognition_nomination_id));

const senderNomination = computed(() => props.nomination?.nominated_by?.[0] || null);

const submitButtonText = computed(() => {
  if (includeReward.value && selectedAmountMinor.value) {
    return `Send ${formatAmount(selectedAmountMinor.value)} to ${props.nomination?.full_name?.split(' ')[0]}`;
  }
  return 'Send recognition';
});

const canSubmit = computed(() => {
  if (isSubmitting.value) {
    return false;
  }
  if (includeReward.value && !selectedAmountMinor.value) {
    return false;
  }
  if (showCategorySelector.value && categoryRequired.value && !selectedCategory.value) {
    return false;
  }
  if (adjustableAmountError.value) {
    return false;
  }
  return true;
});

// Pre-fill the reward from the value the nominator set (the sender nomination) and auto-enable
// it, so the approver sees it ready to grant and can still change it. Adjustable pre-fills the
// typed amount; fixed pre-selects the value only if it's still a valid current preset.
const initRewardFromNomination = () => {
  const nominatorAmount = senderNomination.value?.amount_minor ?? null;
  if (nominatorAmount && isAdjustableAmount.value) {
    includeReward.value = true;
    selectedAmountMinor.value = nominatorAmount;
    typedAwardAmount.value = String(nominatorAmount / 100);
  } else if (nominatorAmount && availableAmounts.value.includes(nominatorAmount)) {
    includeReward.value = true;
    selectedAmountMinor.value = nominatorAmount;
    typedAwardAmount.value = '';
  } else {
    includeReward.value = false;
    selectedAmountMinor.value = null;
    typedAwardAmount.value = '';
  }
};

// Reset the chosen category whenever the bound nomination changes (this also runs on
// show()), so a category selected for a previous nomination can't leak onto a different
// one when the modal instance is reused.
watch(
  () => props.nomination,
  () => {
    selectedCategory.value = null;
    initRewardFromNomination();
  },
);

const show = () => {
  selectedCategory.value = null;
  initRewardFromNomination();
  modalRef.value?.show();
};

const formatAmount = amountMinor => {
  return toCurrency(amountMinor);
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '';
  }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const toggleCategory = category => {
  selectedCategory.value = selectedCategory.value === category ? null : category;
};

const onRewardToggle = checked => {
  // AdjustableRewardInput passes the checkbox state one-way (:include-reward) and emits
  // @toggle, so the handler owns the write-back. The fixed-pills checkboxes still use
  // v-model, for which this assignment is a harmless idempotent no-op.
  includeReward.value = checked;
  if (checked && defaultAmount.value) {
    selectedAmountMinor.value = defaultAmount.value;
  } else if (!checked) {
    selectedAmountMinor.value = null;
    selectedCategory.value = null;
    typedAwardAmount.value = '';
  }
};

const onTypedAmountInput = value => {
  typedAwardAmount.value = value;
  const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  selectedAmountMinor.value = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null;
};

const onReject = async () => {
  errorMessage.value = '';
  isRejecting.value = true;

  try {
    await ApiClient.bulkRejectNominations(allNominationIds.value);
    doToast('Rejected', 'Nomination has been rejected', TOAST_VARIANT.SUCCESS);
    emit('rejected');
    modalRef.value?.hide();
  } catch (e) {
    const data = e?.response?.data || {};
    errorMessage.value = data.message || 'Failed to reject nomination. Please try again.';
  } finally {
    isRejecting.value = false;
  }
};

const onSubmit = async () => {
  errorMessage.value = '';
  isSubmitting.value = true;

  try {
    await ApiClient.bulkApproveNominations(
      allNominationIds.value,
      includeReward.value ? selectedAmountMinor.value : null,
      recipientMessage.value || null,
      selectedCategory.value || null,
    );
    step.value = 'success';
  } catch (e) {
    const data = e?.response?.data || {};
    const errorCode = data.error_code;
    if (errorCode === 'INSUFFICIENT_FUNDS') {
      errorMessage.value = 'Insufficient funds. Please add funds in Settings.';
    } else if (errorCode === 'BUDGET_EXCEEDED') {
      errorMessage.value = 'Budget limit exceeded for this period.';
    } else {
      errorMessage.value = data.message || 'Failed to send award. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
};

const onDone = () => {
  emit('approved');
  modalRef.value?.hide();
};

const resetModal = () => {
  step.value = 1;
  recipientMessage.value = '';
  selectedAmountMinor.value = null;
  typedAwardAmount.value = '';
  includeReward.value = false;
  selectedCategory.value = null;
  rejectOthers.value = false;
  isSubmitting.value = false;
  isRejecting.value = false;
  errorMessage.value = '';
};

defineExpose({ show });
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.step-content {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.back-link {
  font-size: 0.875rem;
  color: var(--palette-color-default-lighten-30);
  text-decoration: none;

  &:hover {
    color: var(--palette-color-default);
  }
}

.nominee-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  &__badge {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__badge-icon {
    color: var(--palette-color-brand-primary);
    font-size: 1.5rem;
  }

  &__name {
    font-weight: 600;
    font-size: 1.125rem;
  }

  &__email {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
  }
}

.nominee-tags {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  &__badge {
    font-weight: 500;
    font-size: 0.75rem;
    padding: 0.25rem 0.625rem;
  }
}

.nomination-messages {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.nomination-message {
  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.375rem;
  }

  &__icon {
    color: var(--palette-color-default-lighten-40);
    font-size: 0.875rem;
  }

  &__author {
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__text {
    margin: 0;
    padding: 0.75rem 1rem;
    background-color: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--palette-color-default);
  }
}

.sender-message {
  margin-bottom: 1.5rem;

  &__label {
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  &__quote {
    padding: 0.75rem 1rem;
    background-color: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--palette-color-default);
  }
}

.section-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--palette-color-default-lighten-30);
  margin-bottom: 0.75rem;
}

.amount-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.amount-pill {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &--selected {
    background-color: var(--palette-color-brand-primary);
    color: var(--palette-color-base-white);
    border-color: var(--palette-color-brand-primary);
  }
}

.value-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.value-chip {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &--selected {
    background-color: var(--palette-color-core-1-lighten-80);
    border-color: var(--palette-color-brand-primary);
    color: var(--palette-color-brand-primary);
  }
}

.success-icon {
  font-size: 4rem;
  color: var(--palette-color-core-5);
}

.footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--right {
    justify-content: flex-end;
  }
}

.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
